import { useState } from 'react'
import {
  Card, Table, Button, Space, Typography, Tag, Row, Col,
  Statistic, Modal, Form, Input, Select, DatePicker, InputNumber,
  Radio, Drawer, message, Alert,
} from 'antd'
import { FileTextOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title, Text } = Typography
const API = ''

const VOUCHER_TYPE_COLORS: Record<string, string> = {
  PV: 'blue', SV: 'green', JV: 'purple', PYV: 'gold',
  RCV: 'cyan', CV: 'default', DN: 'orange', CN: 'volcano',
}

const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'default', PENDING_APPROVAL: 'warning', POSTED: 'success',
}

const VOUCHER_TYPES = ['PV', 'SV', 'JV', 'PYV', 'RCV', 'CV', 'DN', 'CN']

function fmtINR(v: number) {
  return `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
}

export default function VouchersPage() {
  const { plantId, plants, setPlantId } = usePlantId()
  const qc = useQueryClient()

  const [typeFilter, setTypeFilter] = useState<string | undefined>()
  const [statusFilter, setStatusFilter] = useState<string | undefined>()
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null)

  const [viewVoucher, setViewVoucher] = // eslint-disable-next-line @typescript-eslint/no-explicit-any
  useState<any>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [form] = Form.useForm()
  const lines = Form.useWatch('lines', form) ?? []

  const { data: vouchersData, isLoading } = useQuery({
    queryKey: ['vouchers', plantId, typeFilter, statusFilter, dateRange],
    queryFn: () =>
      apiClient.get(`${API}/accounting/vouchers`, {
        params: {
          plant_id: plantId,
          voucher_type: typeFilter || undefined,
          status: statusFilter || undefined,
          from_date: dateRange?.[0]?.format('YYYY-MM-DD') || undefined,
          to_date: dateRange?.[1]?.format('YYYY-MM-DD') || undefined,
          limit: 500,
        },
      }).then(r => r.data),
    enabled: !!plantId,
  })

  const { data: ledgersData } = useQuery({
    queryKey: ['ledgers-all', plantId],
    queryFn: () =>
      apiClient.get(`${API}/accounting/ledgers`, { params: { plant_id: plantId, limit: 1000 } }).then(r => r.data),
    enabled: !!plantId,
  })

  const createMut = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiClient.post(`${API}/accounting/vouchers`, data).then(r => r.data),
    onSuccess: (data: Record<string, unknown>) => {
      qc.invalidateQueries({ queryKey: ['vouchers'] })
      message.success('Voucher created')
      setDrawerOpen(false)
      form.resetFields()
      // auto-post if balanced
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const postMut = useMutation({
    mutationFn: (id: string) =>
      apiClient.post(`${API}/accounting/vouchers/${id}/post`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vouchers'] })
      message.success('Voucher posted')
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const vouchers = Array.isArray(vouchersData) ? vouchersData : []
  const ledgers = Array.isArray(ledgersData) ? ledgersData : []

  const totalVouchers = vouchers.length
  const posted = vouchers.filter((v: any) => v.status === 'POSTED').length
  const pending = vouchers.filter((v: any) => v.status === 'PENDING_APPROVAL').length
  const draft = vouchers.filter((v: any) => v.status === 'DRAFT').length

  // DR/CR balance from form lines
  const drTotal = lines.reduce((s: number, l: any) => s + (l?.dr_cr === 'DR' ? Number(l?.amount ?? 0) : 0), 0)
  const crTotal = lines.reduce((s: number, l: any) => s + (l?.dr_cr === 'CR' ? Number(l?.amount ?? 0) : 0), 0)
  const diff = Math.abs(drTotal - crTotal)
  const isBalanced = diff < 0.01

  const columns = [
    {
      title: 'Voucher No', dataIndex: 'voucher_no', key: 'voucher_no', width: 150,
      render: (v: string) => <b style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</b>,
    },
    {
      title: 'Type', dataIndex: 'voucher_type', key: 'voucher_type', width: 80,
      render: (v: string) => <Tag color={VOUCHER_TYPE_COLORS[v] ?? 'default'}>{v}</Tag>,
    },
    {
      title: 'Date', dataIndex: 'voucher_date', key: 'voucher_date', width: 110,
      render: (v: string) => v ? dayjs(v).format('DD-MMM-YY') : '—',
    },
    {
      title: 'Narration', dataIndex: 'narration', key: 'narration',
      ellipsis: true,
      render: (v: string) => <Text ellipsis={{ tooltip: v }}>{v ?? '—'}</Text>,
    },
    {
      title: 'Amount (₹)', dataIndex: 'total_amount', key: 'total_amount', width: 130, align: 'right' as const,
      render: (v: number) => <span style={{ fontWeight: 600 }}>{fmtINR(v)}</span>,
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 130,
      render: (v: string) => <Tag color={STATUS_COLORS[v] ?? 'default'}>{v?.replace('_', ' ')}</Tag>,
    },
    {
      title: 'Auto', dataIndex: 'is_auto_posted', key: 'is_auto_posted', width: 70, align: 'center' as const,
      render: (v: boolean) => v ? <Tag color="processing">AUTO</Tag> : null,
    },
    {
      title: 'Actions', key: 'actions', width: 140,
      render: (_: unknown, r: any) => (
        <Space>
          <Button size="small" onClick={() => setViewVoucher(r)}>View</Button>
          {r.status === 'DRAFT' && (
            <Button size="small" type="primary" loading={postMut.isPending} onClick={() => postMut.mutate(r.id)}>Post</Button>
          )}
        </Space>
      ),
    },
  ]

  const lineColumns = [
    {
      title: 'Ledger', key: 'ledger_name', width: 200,
      render: (_: unknown, r: any) => r.ledger_name ?? r.ledger?.ledger_name ?? '—',
    },
    {
      title: 'Dr/Cr', dataIndex: 'dr_cr', key: 'dr_cr', width: 80,
      render: (v: string) => (
        <b style={{ color: v === 'DR' ? '#52c41a' : '#ff4d4f' }}>{v}</b>
      ),
    },
    {
      title: 'Amount (₹)', dataIndex: 'amount', key: 'amount', width: 130, align: 'right' as const,
      render: (v: number) => fmtINR(v),
    },
    { title: 'Cost Center', dataIndex: 'cost_center', key: 'cost_center', width: 120 },
    { title: 'Narration', dataIndex: 'narration', key: 'narration', ellipsis: true },
  ]

  const vLines: any[] = viewVoucher?.lines ?? []
  const vDr = vLines.reduce((s: number, l: any) => s + (l.dr_cr === 'DR' ? Number(l.amount ?? 0) : 0), 0)
  const vCr = vLines.reduce((s: number, l: any) => s + (l.dr_cr === 'CR' ? Number(l.amount ?? 0) : 0), 0)

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 0 }}>
        <FileTextOutlined style={{ marginRight: 8, color: '#1677ff' }} />
        Vouchers
      </Title>
      <Text type="secondary">Journal entries, payments, receipts and other accounting vouchers</Text>

      {/* Stats */}
      <Row gutter={16} style={{ marginTop: 16 }}>
        {[
          { title: 'Total Vouchers', value: totalVouchers, color: '#1677ff' },
          { title: 'Posted', value: posted, color: '#52c41a' },
          { title: 'Pending', value: pending, color: '#faad14' },
          { title: 'Draft', value: draft, color: '#999' },
        ].map((s, i) => (
          <Col span={6} key={i}>
            <Card size="small">
              <Statistic title={s.title} value={s.value} valueStyle={{ color: s.color }} />
            </Card>
          </Col>
        ))}
      </Row>

      <Card style={{ marginTop: 16 }}>
        {/* Filter Bar */}
        <Row gutter={12} style={{ marginBottom: 16 }} align="middle">
          <Col>
            <Select
              value={plantId}
              onChange={setPlantId}
              style={{ width: 180 }}
              options={plants.map((p: any) => ({ value: p.id, label: p.name ?? p.plant_name }))}
            />
          </Col>
          <Col>
            <Select
              allowClear
              placeholder="Type"
              style={{ width: 120 }}
              value={typeFilter}
              onChange={setTypeFilter}
              options={[{ value: '', label: 'All Types' }, ...VOUCHER_TYPES.map(t => ({ value: t, label: t }))]}
            />
          </Col>
          <Col>
            <Select
              allowClear
              placeholder="Status"
              style={{ width: 160 }}
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: 'DRAFT', label: 'Draft' },
                { value: 'PENDING_APPROVAL', label: 'Pending Approval' },
                { value: 'POSTED', label: 'Posted' },
              ]}
            />
          </Col>
          <Col>
            <DatePicker.RangePicker
              onChange={(v) => setDateRange(v as any)}
              style={{ width: 240 }}
            />
          </Col>
          <Col flex="auto" style={{ textAlign: 'right' }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => { form.resetFields(); setDrawerOpen(true) }}
            >
              New Voucher
            </Button>
          </Col>
        </Row>

        <Table
          dataSource={vouchers}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          size="small"
          pagination={{ pageSize: 50, showSizeChanger: true }}
          scroll={{ x: 1000 }}
        />
      </Card>

      {/* View Voucher Modal */}
      <Modal
        title={`Voucher — ${viewVoucher?.voucher_no}`}
        open={!!viewVoucher}
        onCancel={() => setViewVoucher(null)}
        footer={<Button onClick={() => setViewVoucher(null)}>Close</Button>}
        width={800}
      >
        {viewVoucher && (
          <>
            <Row gutter={16} style={{ marginBottom: 12 }}>
              <Col span={8}><Text type="secondary">Date: </Text><Text strong>{dayjs(viewVoucher.voucher_date).format('DD-MMM-YYYY')}</Text></Col>
              <Col span={8}><Text type="secondary">Type: </Text><Tag color={VOUCHER_TYPE_COLORS[viewVoucher.voucher_type] ?? 'default'}>{viewVoucher.voucher_type}</Tag></Col>
              <Col span={8}><Text type="secondary">Status: </Text><Tag color={STATUS_COLORS[viewVoucher.status] ?? 'default'}>{viewVoucher.status}</Tag></Col>
            </Row>
            {viewVoucher.narration && <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>{viewVoucher.narration}</Text>}
            <Table
              dataSource={vLines}
              columns={lineColumns}
              rowKey={(r, i) => String(i)}
              size="small"
              pagination={false}
            />
            <Row style={{ marginTop: 12, padding: '8px 0', borderTop: '1px solid #f0f0f0' }}>
              <Col span={12}>
                <Text strong style={{ color: '#52c41a' }}>DR Total: {fmtINR(vDr)}</Text>
              </Col>
              <Col span={12} style={{ textAlign: 'right' }}>
                <Text strong style={{ color: '#ff4d4f' }}>CR Total: {fmtINR(vCr)}</Text>
              </Col>
            </Row>
          </>
        )}
      </Modal>

      {/* New Voucher Drawer */}
      <Drawer
        title="New Voucher"
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); form.resetFields() }}
        width={700}
        footer={
          <Space style={{ float: 'right' }}>
            <Button onClick={() => { setDrawerOpen(false); form.resetFields() }}>Cancel</Button>
            <Button
              type="primary"
              loading={createMut.isPending}
              disabled={!isBalanced || lines.length < 2}
              onClick={() =>
                form.validateFields().then(vals =>
                  createMut.mutate({
                    ...vals,
                    plant_id: plantId,
                    voucher_date: vals.voucher_date?.format('YYYY-MM-DD'),
                  })
                )
              }
            >
              Save Voucher
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="voucher_type" label="Voucher Type" rules={[{ required: true }]}>
                <Select options={VOUCHER_TYPES.map(t => ({ value: t, label: t }))} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="voucher_date" label="Date" rules={[{ required: true }]} initialValue={dayjs()}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="narration" label="Narration">
            <Input.TextArea rows={2} />
          </Form.Item>

          <div style={{ marginBottom: 8 }}>
            <Text strong>Voucher Lines</Text>
          </div>

          {/* DR/CR balance indicator */}
          {lines.length > 0 && (
            <Alert
              type={isBalanced ? 'success' : 'warning'}
              message={
                <Space>
                  <span>DR: {fmtINR(drTotal)}</span>
                  <span>CR: {fmtINR(crTotal)}</span>
                  {!isBalanced && <span style={{ color: 'red' }}>Diff: {fmtINR(diff)}</span>}
                  {isBalanced && <span style={{ color: '#52c41a' }}>Balanced</span>}
                </Space>
              }
              style={{ marginBottom: 12 }}
            />
          )}

          <Form.List name="lines">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...rest }) => (
                  <Card key={key} size="small" style={{ marginBottom: 8, background: '#fafafa' }}>
                    <Row gutter={8} align="middle">
                      <Col span={10}>
                        <Form.Item
                          {...rest}
                          name={[name, 'ledger_id']}
                          label="Ledger"
                          rules={[{ required: true }]}
                          style={{ marginBottom: 0 }}
                        >
                          <Select
                            showSearch
                            optionFilterProp="label"
                            placeholder="Select ledger"
                            options={ledgers.map((l: any) => ({ value: l.id, label: `${l.ledger_code} - ${l.ledger_name}` }))}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item
                          {...rest}
                          name={[name, 'dr_cr']}
                          label="Dr/Cr"
                          rules={[{ required: true }]}
                          style={{ marginBottom: 0 }}
                          initialValue="DR"
                        >
                          <Radio.Group size="small">
                            <Radio.Button value="DR">DR</Radio.Button>
                            <Radio.Button value="CR">CR</Radio.Button>
                          </Radio.Group>
                        </Form.Item>
                      </Col>
                      <Col span={5}>
                        <Form.Item
                          {...rest}
                          name={[name, 'amount']}
                          label="Amount"
                          rules={[{ required: true }]}
                          style={{ marginBottom: 0 }}
                        >
                          <InputNumber style={{ width: '100%' }} min={0} precision={2} />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item {...rest} name={[name, 'cost_center']} label="Cost Center" style={{ marginBottom: 0 }}>
                          <Input placeholder="Optional" />
                        </Form.Item>
                      </Col>
                      <Col span={1}>
                        <Button
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={() => remove(name)}
                          style={{ marginTop: 24 }}
                        />
                      </Col>
                    </Row>
                    <Row gutter={8} style={{ marginTop: 8 }}>
                      <Col span={23}>
                        <Form.Item {...rest} name={[name, 'narration']} label="Line Narration" style={{ marginBottom: 0 }}>
                          <Input placeholder="Optional" />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                ))}
                <Button
                  type="dashed"
                  block
                  icon={<PlusOutlined />}
                  onClick={() => add({ dr_cr: 'DR', amount: 0 })}
                >
                  Add Line
                </Button>
              </>
            )}
          </Form.List>
        </Form>
      </Drawer>
    </div>
  )
}
